import styled from "styled-components";

export const PageRoot = styled.main`
  width: 100%;
  min-height: 100vh;
  padding: 112px clamp(18px, 4vw, 56px) 72px;
  background:
    radial-gradient(circle at 50% 0%, ${p => p.theme.palette.colors.shellGradientStart} 0%, ${p => p.theme.palette.colors.shellGradientEnd} 42%),
    ${p => p.theme.palette.colors.background};

  @media (max-width: 991px) {
    padding: 96px 18px 48px;
  }

  @media (max-width: 480px) {
    padding: 88px 12px 36px;
  }
`;

export const Content = styled.div`
  width: min(1120px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

export const QuizHeader = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(220px, 320px);
  gap: 40px;
  align-items: end;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

export const HeaderCopy = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 5px 11px;
  border-radius: 999px;
  background: ${p => p.theme.palette.colors.badgeMuted};
  color: ${p => p.theme.palette.colors.muted};
  font-family: Poppins;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

export const QuizTitle = styled.h1`
  margin: 0;
  max-width: 800px;
  color: ${p => p.theme.palette.colors.text};
  font-family: Gilroy;
  font-size: clamp(34px, 5vw, 54px);
  font-weight: 700;
  line-height: 1.04;
`;

export const QuizDescription = styled.p`
  margin: 0;
  max-width: 760px;
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.6;
`;

export const ProgressBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 18px 20px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 16px;
  background: ${p => p.theme.palette.colors.surfaceElevatedSoft};
  box-shadow: 0 18px 46px ${p => p.theme.palette.colors.shadowStrong};
`;

export const ProgressMeta = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  color: ${p => p.theme.palette.colors.muted};
  font-family: Poppins;
  font-size: 13px;
  font-weight: 600;
`;

export const ProgressValue = styled.strong`
  color: ${p => p.theme.palette.colors.text};
  font-size: 18px;
  font-weight: 700;
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: ${p => p.theme.palette.colors.buttonGhost};
`;

export const ProgressFill = styled.div`
  width: ${p => `${p.$progress}%`};
  height: 100%;
  border-radius: inherit;
  background: ${p => p.theme.palette.blue.main};
  transition: width 0.35s ease;
`;

export const QuestionCard = styled.section`
  overflow: hidden;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 24px;
  background:
    linear-gradient(145deg, ${p => p.theme.palette.colors.surfaceElevated} 0%, ${p => p.theme.palette.colors.surfaceElevatedStrong} 100%);
  box-shadow: 0 28px 76px ${p => p.theme.palette.colors.shadowStrong};
  animation: questionReveal 0.32s ease both;

  @keyframes questionReveal {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const QuestionContent = styled.div`
  padding: clamp(24px, 4vw, 42px);
`;

export const QuestionHeading = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 28px;
  align-items: start;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const QuestionLabel = styled.div`
  margin-bottom: 10px;
  color: ${p => p.theme.palette.colors.subdued};
  font-family: Poppins;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
`;

export const QuestionTitle = styled.h2`
  margin: 0;
  max-width: 820px;
  color: ${p => p.theme.palette.colors.text};
  font-family: Gilroy;
  font-size: clamp(24px, 3vw, 34px);
  font-weight: 700;
  line-height: 1.24;
`;

export const QuestionCounter = styled.div`
  min-width: 86px;
  padding: 9px 13px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 12px;
  background: ${p => p.theme.palette.colors.buttonGhost};
  color: ${p => p.theme.palette.colors.text};
  font-family: Poppins;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;

  @media (max-width: 600px) {
    width: fit-content;
  }
`;

export const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 30px;
`;

export const QuestionFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 20px clamp(24px, 4vw, 42px);
  border-top: 1px solid ${p => p.theme.palette.colors.borderSoft};
  background: ${p => p.theme.palette.colors.surfaceElevatedSoft};

  @media (max-width: 700px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const AnswerHint = styled.p`
  margin: 0;
  color: ${p => p.theme.palette.colors.subdued};
  font-family: Lato;
  font-size: 14px;
  line-height: 1.5;
`;

export const QuizActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  @media (max-width: 700px) {
    & > * {
      flex: 1;
    }
  }

  @media (max-width: 420px) {
    flex-direction: column-reverse;

    & > * {
      width: 100%;
    }
  }
`;

export const LoadingPanel = styled.div`
  width: min(1120px, 100%);
  min-height: 280px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 24px;
  background: ${p => p.theme.palette.colors.surfaceElevatedSoft};
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato;
  font-size: 15px;
`;

export const ResultContainer = styled.section`
  width: min(860px, 100%);
  margin: 0 auto;
  padding: clamp(28px, 5vw, 48px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 24px;
  background:
    linear-gradient(145deg, ${p => p.theme.palette.colors.surfaceElevated} 0%, ${p => p.theme.palette.colors.surfaceElevatedStrong} 100%);
  box-shadow: 0 28px 76px ${p => p.theme.palette.colors.shadowStrong};
  text-align: center;
`;

export const ResultMark = styled.div`
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${p => p.$pending ? p.theme.palette.colors.warningMuted : p.$success ? p.theme.palette.colors.successMuted : p.theme.palette.colors.buttonGhost};
  border: 1px solid ${p => p.$pending ? p.theme.palette.warning.main : p.$success ? p.theme.palette.success.main : p.theme.palette.colors.borderStrong};
  color: ${p => p.$pending ? p.theme.palette.warning.main : p.$success ? p.theme.palette.success.main : p.theme.palette.colors.muted};
  font-family: Poppins;
  font-size: 25px;
  font-weight: 800;
`;

export const ResultTitle = styled.h2`
  margin: 0;
  color: ${p => p.theme.palette.colors.text};
  font-family: Gilroy;
  font-size: clamp(28px, 4vw, 38px);
  font-weight: 700;
  line-height: 1.2;
`;

export const ResultText = styled.p`
  margin: 0;
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.6;
`;

export const ResultCallout = styled.div`
  width: min(560px, 100%);
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 18px 20px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 14px;
  background: ${p => p.theme.palette.colors.warningMuted};
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato;
  font-size: 15px;
  line-height: 1.5;

  strong {
    color: ${p => p.theme.palette.colors.text};
    font-family: Poppins;
    font-size: 18px;
  }
`;

export const ErrorList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 4px 0;
  text-align: left;
`;

export const ErrorItem = styled.div`
  padding: 16px 18px;
  border: 1px solid ${p => p.theme.palette.colors.borderSoft};
  border-radius: 14px;
  background: ${p => p.theme.palette.colors.buttonGhost};
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato;
  font-size: 14px;
  line-height: 1.55;

  strong {
    display: block;
    margin-bottom: 5px;
    color: ${p => p.theme.palette.colors.text};
    font-size: 15px;
  }
`;
